/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Button, Box, InputAdornment, LinearProgress } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import {
  defaultFormOptions,
  ReactHookFormProvider,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import { CustomDialog, CustomIconButton } from '@opengeoweb/shared';
import type { serviceTypes } from '@opengeoweb/store';
import { Copy } from '@opengeoweb/theme';
import type { ValidationResult } from './utils';
import {
  loadWMSService,
  loadEDRService,
  loadSTACService,
  detectServiceType,
  normalizeStacServiceUrl,
  validateServiceName,
  validateServiceUrl,
  DetectableServiceType,
} from './utils';
import type { layerSelectTypes } from '../../store';
import { useLayerSelectTranslation } from '../../utils/i18n';

const style = {
  textField: {
    width: '100%',
    marginBottom: 2,
  },
};

export interface ServicePopupProps {
  servicePopupVariant: layerSelectTypes.PopupVariant;
  hideBackdrop?: boolean;
  isOpen?: boolean;
  closePopup?: () => void;
  shouldDisableAutoFocus?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  serviceId?: string;
  serviceUrl?: string;
  serviceName?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  serviceAbstracts?: string;
  services?: layerSelectTypes.ActiveServiceObjectEntities;
  serviceSetLayers: (payload: serviceTypes.SetLayersForServicePayload) => void;
  showSnackbar?: (message: string) => void;
  disableAutoFocus?: boolean;
}

export const getSuccesMessage = (serviceName: string, t: TFunction): string =>
  t('get-success-message', { serviceName });

const getTitleForVariant = (
  t: TFunction,
  variant: layerSelectTypes.PopupVariant,
): string => {
  switch (variant) {
    case 'add':
      return t('add-a-new-service');
    case 'edit':
      return t('edit-service-title');
    case 'save':
      return t('save-service-title');
    default:
      return 'Service';
  }
};

interface InitialValidationsTriggerProps {
  trigger: UseFormTrigger<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  onValidField: (value: string) => void;
}

enum FormFields {
  serviceUrl = 'serviceUrl',
  serviceName = 'serviceName',
  serviceAbstracts = 'serviceAbstracts',
}
type FormValues = Record<FormFields, string>;

const InitialValidationsTrigger: React.FC<InitialValidationsTriggerProps> = ({
  trigger,
  getValues,
  onValidField,
}: InitialValidationsTriggerProps) => {
  React.useEffect(() => {
    const value = getValues(FormFields.serviceUrl);
    if (value) {
      void trigger(FormFields.serviceUrl).then(
        (isValid) => isValid && onValidField(value),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const ServicePopupUnwrapped: React.FC<ServicePopupProps> = ({
  servicePopupVariant,
  hideBackdrop = false,
  isOpen,
  closePopup,
  shouldDisableAutoFocus,
  services,
  serviceSetLayers,
  serviceUrl,
  serviceName,
  showSnackbar = (): void => {},
  disableAutoFocus = false,
}: ServicePopupProps) => {
  const { t } = useLayerSelectTranslation();
  const {
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
    watch,
    trigger,
    getValues,
    setValue,
  } = useFormContext<FormValues>();

  const [isLoading, setIsLoading] = React.useState(false);
  const [shouldForceReload, setShouldForceReload] = React.useState(false);
  const [detectedServiceType, setDetectedServiceType] =
    React.useState<DetectableServiceType | null>(null);

  const fetchServiceData = async (newServiceUrl: string): Promise<void> => {
    try {
      setIsLoading(true);

      const type = await detectServiceType(newServiceUrl);
      setDetectedServiceType(type);

      const serviceUrlForLoading =
        type === DetectableServiceType.STAC
          ? normalizeStacServiceUrl(newServiceUrl)
          : newServiceUrl;

      if (serviceUrlForLoading !== newServiceUrl) {
        setValue(FormFields.serviceUrl, serviceUrlForLoading);
      }

      let service;
      if (type === DetectableServiceType.STAC) {
        service = await loadSTACService(serviceUrlForLoading);
      } else if (type === DetectableServiceType.EDR) {
        service = await loadEDRService(serviceUrlForLoading);
      } else {
        service = await loadWMSService(serviceUrlForLoading, true);
      }

      setValue(FormFields.serviceName, service.name);
      setValue(FormFields.serviceAbstracts, service.abstract || '');
      setIsLoading(false);
      setShouldForceReload(false);
    } catch (_error) {
      setIsLoading(false);
      setShouldForceReload(true);
      setError(FormFields.serviceUrl, {
        message: t('validations-request-failed'),
        type: 'SYSTEM',
      });
    }
  };

  const addNewService = async (formValues: FormValues): Promise<void> => {
    clearErrors(FormFields.serviceUrl);
    setIsLoading(true);
    try {
      const type =
        detectedServiceType ?? (await detectServiceType(formValues.serviceUrl));

      const serviceUrlForSaving =
        type === DetectableServiceType.STAC
          ? normalizeStacServiceUrl(formValues.serviceUrl)
          : formValues.serviceUrl;

      let service;
      if (type === DetectableServiceType.STAC) {
        service = await loadSTACService(serviceUrlForSaving);
      } else if (type === DetectableServiceType.EDR) {
        service = await loadEDRService(serviceUrlForSaving);
      } else {
        service = await loadWMSService(serviceUrlForSaving, shouldForceReload);
      }

      if (serviceUrlForSaving !== formValues.serviceUrl) {
        setValue(FormFields.serviceUrl, serviceUrlForSaving);
      }

      serviceSetLayers({
        id: service.id,
        name: formValues.serviceName,
        serviceUrl: serviceUrlForSaving,
        abstract: formValues.serviceAbstracts,
        layers: service.layers,
        scope: 'user',
        type: service.type,
      });
      setIsLoading(false);
      showSnackbar(getSuccesMessage(formValues.serviceName, t));
      closePopup!();
    } catch (_error) {
      setIsLoading(false);
      setError(FormFields.serviceUrl, {
        message: t('validations-request-failed'),
        type: 'SYSTEM',
      });
    }
  };

  const onValidateServiceUrl = (value: string): ValidationResult =>
    validateServiceUrl(t, value, services!);

  const onFocusServiceUrl = (): void => {
    const hasValue = getValues(FormFields.serviceUrl);
    if (!errors[FormFields.serviceUrl] && hasValue) {
      void trigger(FormFields.serviceUrl);
    }
  };

  const onBlurServiceUrl = (): void => {
    void trigger(FormFields.serviceUrl).then(() => {
      const hasValue = getValues(FormFields.serviceUrl);
      if (!errors[FormFields.serviceUrl] && hasValue) {
        void fetchServiceData(getValues(FormFields.serviceUrl));
      }
    });
  };

  const onKeyDownServiceUrl = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      onBlurServiceUrl();
    }
  };

  const onValidateServiceName = (value: string): ValidationResult =>
    validateServiceName(t, value, services!, serviceName);

  const getFormAreRequiredFieldsEmpty = (): boolean => {
    const serviceNameField = watch(FormFields.serviceName);
    const serviceUrlField = watch(FormFields.serviceUrl);

    return (
      serviceNameField === '' ||
      serviceNameField === undefined ||
      serviceUrlField === '' ||
      serviceUrlField === undefined
    );
  };

  const getFormHasformErrors = (): boolean => {
    const totalErrors = Object.keys(errors).length;
    if (totalErrors > 0) {
      // prevent SYSTEM error types from blocking the save button
      if (totalErrors === 1 && errors.serviceUrl) {
        return (
          errors.serviceUrl.type !== undefined &&
          errors.serviceUrl.type !== 'SYSTEM'
        );
      }
      return true;
    }
    return false;
  };

  const isSubmitDisabled =
    getFormHasformErrors() || getFormAreRequiredFieldsEmpty();

  const isShowPopup = servicePopupVariant === 'show';
  const isServiceUrlDisabled = isShowPopup || servicePopupVariant === 'edit';

  return (
    <CustomDialog
      actions={
        isShowPopup ? (
          <Button
            onClick={(): void => closePopup!()}
            sx={{ width: { xs: '100%', sm: '117px' } }}
            variant="tertiary"
          >
            {t('close')}
          </Button>
        ) : (
          <>
            <Button
              data-testid="closePopupButtonCancel"
              onClick={closePopup}
              sx={{ width: { xs: '100%', sm: '117px' } }}
              variant="tertiary"
            >
              {t('cancel')}
            </Button>
            <Button
              data-testid="saveServiceButton"
              disabled={isSubmitDisabled || isLoading}
              form="servicePopupForm"
              sx={{
                marginLeft: {
                  xs: '0px!important',
                  sm: '8px!important',
                },
                width: {
                  xs: '100%',
                  sm: '117px',
                },
              }}
              type="submit"
              variant="primary"
            >
              {t('save')}
            </Button>
          </>
        )
      }
      data-testid="servicePopup"
      disableAutoFocus={disableAutoFocus}
      fullWidth
      hideBackdrop={hideBackdrop}
      maxWidth="xs"
      onClose={() => closePopup?.()}
      open={isOpen!}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: `geowebColors.cards.cardContainer`,
          maxWidth: 458,
        },
      }}
      title={getTitleForVariant(t, servicePopupVariant)}
    >
      <form
        id="servicePopupForm"
        onSubmit={handleSubmit(async (formValues) =>
          addNewService(formValues as FormValues),
        )}
      >
        {!!isLoading && (
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              color="secondary"
              data-testid="loadingIndicator"
              sx={{ position: 'absolute', width: '100%', top: 8 }}
            />
          </Box>
        )}
        <ReactHookFormTextField
          autoFocus={!shouldDisableAutoFocus}
          defaultValue={serviceUrl}
          disabled={isServiceUrlDisabled || isLoading}
          inputSlotProps={{
            disableUnderline: isShowPopup,
            endAdornment: isShowPopup && (
              <InputAdornment position="end">
                <CustomIconButton
                  aria-label="copy url"
                  onClick={(): void => {
                    void navigator.clipboard.writeText(serviceUrl!);
                    showSnackbar(t('copy-url-message'));
                  }}
                  sx={{ marginTop: 0.5 }}
                >
                  <Copy />
                </CustomIconButton>
              </InputAdornment>
            ),
          }}
          isReadOnly={isShowPopup}
          label={t('service-url-label')}
          name={FormFields.serviceUrl}
          onBlur={onBlurServiceUrl}
          onFocus={onFocusServiceUrl}
          onKeyDown={onKeyDownServiceUrl}
          rules={{
            required: true,
            validate: {
              ...(!isServiceUrlDisabled && {
                onValidateServiceUrl,
              }),
            },
          }}
          sx={style.textField}
        />

        <ReactHookFormTextField
          defaultValue={serviceName}
          disabled={isShowPopup || isLoading}
          inputSlotProps={{
            disableUnderline: isShowPopup,
          }}
          isReadOnly={isShowPopup}
          label={t('service-name-label')}
          name={FormFields.serviceName}
          rules={{
            required: true,
            validate: {
              onValidateServiceName,
            },
          }}
          sx={style.textField}
        />

        <ReactHookFormTextField
          defaultValue=""
          disabled={isShowPopup || isLoading}
          helperText={!isShowPopup ? t('service-popup-helper-text') : ''}
          inputSlotProps={{
            disableUnderline: isShowPopup,
          }}
          isReadOnly={isShowPopup}
          label={t('service-abstracts-label')}
          multiline
          name={FormFields.serviceAbstracts}
          rows={4}
          sx={style.textField}
        />

        {!isServiceUrlDisabled && (
          <InitialValidationsTrigger
            getValues={getValues}
            onValidField={fetchServiceData}
            trigger={trigger}
          />
        )}
      </form>
    </CustomDialog>
  );
};

export const ServicePopup: React.FC<ServicePopupProps> = ({
  ...props
}: ServicePopupProps) => (
  <ReactHookFormProvider
    options={{
      ...defaultFormOptions,
      defaultValues: {
        serviceUrl: props.serviceUrl,
        serviceName: props.serviceName,
        serviceAbstracts: props.serviceAbstracts,
      },
    }}
  >
    <ServicePopupUnwrapped {...props} />
  </ReactHookFormProvider>
);
